import { useState, useEffect, useCallback } from "react";
import { X, AlertTriangle, TrendingDown, ArrowRight, Zap, Clock } from "lucide-react";

const EXIT_MESSAGES = {
  en: { h1: "Wait!", h2: "Your website is losing", amount: "€1,230", period: "/month", desc: "without AI visibility. 73% of websites are invisible to ChatGPT, Perplexity & Gemini.", cta: "GET FREE SCAN NOW", sub: "30 seconds. No credit card. Instant results.", close: "I don't care about lost revenue", discount: "75% OFF — limited time only" },
  sk: { h1: "Počkajte!", h2: "Váš web stráca", amount: "€1 230", period: "/mesiac", desc: "bez AI viditeľnosti. 73% webov je neviditeľných pre ChatGPT, Perplexity a Gemini.", cta: "BEZPLATNÝ SKEN TERAZ", sub: "30 sekúnd. Bez kreditnej karty. Okamžité výsledky.", close: "Nezaujíma ma stratený príjem", discount: "75% ZĽAVA — limitovaná ponuka" },
  cs: { h1: "Počkejte!", h2: "Váš web ztrácí", amount: "€1 230", period: "/měsíc", desc: "bez AI viditelnosti. 73% webů je neviditelných pro ChatGPT, Perplexity a Gemini.", cta: "BEZPLATNÝ SKEN", sub: "30 sekund. Bez kreditní karty.", close: "Nezajímá mě ztracený příjem", discount: "75% SLEVA — limitovaná nabídka" },
  de: { h1: "Moment!", h2: "Ihre Website verliert", amount: "€1.230", period: "/Monat", desc: "ohne AI-Sichtbarkeit. 73% der Websites sind für ChatGPT, Perplexity & Gemini unsichtbar.", cta: "KOSTENLOSER SCAN", sub: "30 Sekunden. Keine Kreditkarte.", close: "Mich interessiert das nicht", discount: "75% RABATT — nur für kurze Zeit" },
  fr: { h1: "Attendez!", h2: "Votre site perd", amount: "€1 230", period: "/mois", desc: "sans visibilité IA. 73% des sites sont invisibles pour ChatGPT, Perplexity & Gemini.", cta: "SCAN GRATUIT", sub: "30 secondes. Sans carte de crédit.", close: "Ça ne m'intéresse pas", discount: "75% DE RÉDUCTION — temps limité" },
  es: { h1: "¡Espere!", h2: "Su web pierde", amount: "€1.230", period: "/mes", desc: "sin visibilidad IA. 73% de webs son invisibles para ChatGPT, Perplexity y Gemini.", cta: "ESCANEO GRATIS", sub: "30 segundos. Sin tarjeta de crédito.", close: "No me interesa", discount: "75% DTO — tiempo limitado" },
  pl: { h1: "Chwila!", h2: "Twoja strona traci", amount: "€1 230", period: "/miesiąc", desc: "bez widoczności AI. 73% stron jest niewidocznych dla ChatGPT, Perplexity i Gemini.", cta: "DARMOWY SKAN", sub: "30 sekund. Bez karty kredytowej.", close: "Nie interesuje mnie to", discount: "75% ZNIŻKI — oferta ograniczona" },
  hu: { h1: "Várjon!", h2: "Weboldala elveszít", amount: "€1 230", period: "/hó", desc: "AI láthatóság nélkül. A weboldalak 73%-a láthatatlan a ChatGPT, Perplexity és Gemini számára.", cta: "INGYENES VIZSGÁLAT", sub: "30 másodperc. Bankkártya nélkül.", close: "Nem érdekel", discount: "75% KEDVEZMÉNY — korlátozott ideig" },
  it: { h1: "Aspetta!", h2: "Il tuo sito perde", amount: "€1.230", period: "/mese", desc: "senza visibilità AI. Il 73% dei siti è invisibile per ChatGPT, Perplexity e Gemini.", cta: "ANALISI GRATUITA", sub: "30 secondi. Senza carta di credito.", close: "Non mi interessa", discount: "75% SCONTO — tempo limitato" },
  pt: { h1: "Espere!", h2: "Seu site perde", amount: "€1.230", period: "/mês", desc: "sem visibilidade AI. 73% dos sites são invisíveis para ChatGPT, Perplexity e Gemini.", cta: "ANÁLISE GRÁTIS", sub: "30 segundos. Sem cartão de crédito.", close: "Não me interessa", discount: "75% DESC. — tempo limitado" },
  ja: { h1: "お待ちください！", h2: "あなたのサイトは失っています", amount: "€1,230", period: "/月", desc: "AI可視性なし。ウェブサイトの73%がChatGPT、Perplexity、Geminiに見えません。", cta: "無料スキャン", sub: "30秒。クレジットカード不要。", close: "興味がありません", discount: "75%オフ — 期間限定" },
  ko: { h1: "잠깐!", h2: "귀하의 사이트는 잃고 있습니다", amount: "€1,230", period: "/월", desc: "AI 가시성 없이. 웹사이트의 73%가 ChatGPT, Perplexity, Gemini에 보이지 않습니다.", cta: "무료 스캔", sub: "30초. 신용카드 불필요.", close: "관심 없습니다", discount: "75% 할인 — 한정 시간" },
  zh: { h1: "等一下！", h2: "您的网站正在损失", amount: "€1,230", period: "/月", desc: "没有AI可见性。73%的网站对ChatGPT、Perplexity和Gemini不可见。", cta: "免费扫描", sub: "30秒。无需信用卡。", close: "不感兴趣", discount: "75%折扣 — 限时优惠" },
};

export function ExitIntentPopup({ language = "en", onScan }) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 5 && !dismissed && !sessionStorage.getItem("trp_exit_shown")) {
      setShow(true);
      sessionStorage.setItem("trp_exit_shown", "1");
    }
  }, [dismissed]);

  useEffect(() => {
    // Only show on desktop (mobile has no mouse leave)
    if (window.innerWidth < 768) return;
    // Don't show if already shown this session
    if (sessionStorage.getItem("trp_exit_shown")) return;

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [handleMouseLeave]);

  const handleClose = () => {
    setShow(false);
    setDismissed(true);
  };

  const handleScan = () => {
    setShow(false);
    setDismissed(true);
    if (onScan) onScan();
    // Scroll to top where the scan input is
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      const input = document.querySelector('[data-testid="hero-url-input"]');
      if (input) input.focus();
    }, 500);
  };

  const t = EXIT_MESSAGES[language] || EXIT_MESSAGES.en;

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-testid="exit-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[var(--color-bg)]/80 backdrop-blur-sm" onClick={handleClose} />

      {/* Popup */}
      <div className="relative bg-[var(--color-bg)] border-2 border-[#a855f7] max-w-lg w-full animate-in zoom-in-95 shadow-2xl shadow-purple-900/20" data-testid="exit-popup-content">
        {/* Close */}
        <button onClick={handleClose} className="absolute top-4 right-4 text-[var(--color-text-muted)] hover:text-[var(--color-text)] z-10" data-testid="exit-popup-close">
          <X className="w-5 h-5" />
        </button>

        {/* Red top bar */}
        <div className="bg-[#a855f7] py-3 px-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-white animate-pulse" />
          <span className="text-white font-black font-['Cabinet_Grotesk'] text-lg tracking-tight">{t.h1}</span>
        </div>

        {/* Content */}
        <div className="p-8 text-center">
          <p className="text-[var(--color-text-muted)] text-sm mb-2 font-['JetBrains_Mono'] uppercase tracking-wider">{t.h2}</p>
          <div className="flex items-baseline justify-center gap-1 mb-4">
            <TrendingDown className="w-8 h-8 text-[#a855f7]" />
            <span className="text-6xl font-black font-['JetBrains_Mono'] text-[#a855f7]">{t.amount}</span>
            <span className="text-xl text-[var(--color-text-muted)] font-bold">{t.period}</span>
          </div>
          <p className="text-[var(--color-text-muted)] text-sm mb-8 max-w-sm mx-auto leading-relaxed">{t.desc}</p>

          {/* Urgency timer */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Clock className="w-4 h-4 text-[#FFE600]" />
            <span className="text-[#FFE600] text-sm font-['JetBrains_Mono'] font-bold">{t.discount || "75% OFF — limited time only"}</span>
          </div>

          {/* CTA */}
          <button
            onClick={handleScan}
            className="w-full py-5 bg-[#a855f7] text-white font-black text-xl uppercase tracking-wider hover:bg-purple-600 transition-all flex items-center justify-center gap-3 mb-3"
            data-testid="exit-popup-cta"
          >
            <Zap className="w-6 h-6" />{t.cta}
          </button>
          <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono']">{t.sub}</p>

          {/* Dismiss link */}
          <button
            onClick={handleClose}
            className="mt-6 text-zinc-700 text-xs hover:text-[var(--color-text-muted)] transition-colors underline underline-offset-2"
            data-testid="exit-popup-dismiss"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
}
